import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Heart, Bed, Bath, Car, Maximize, MapPin, Calendar, Phone, Mail, ChevronLeft, ChevronRight, Camera, X, Calculator, Check, Star, BadgeCheck, Flag, Building2, PlayCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InspectionModal from '../components/InspectionModal';
import ContactAgentModal from '../components/ContactAgentModal';
import ReportPropertyModal from '../components/ReportPropertyModal';
import ShareButton from '../components/ShareButton';
import { formatCOP } from '../mock';
import { Button } from '../components/ui/button';
import { api, imgUrl, thumbUrl } from '../lib/api';
import { isDual, getPrices, operationLabel, evChargerLabel } from '../lib/operations';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { formatCOP as fmt } from '../mock';
import { track, EventTypes } from '../lib/analytics';
import advisor from '../lib/advisor';
import AdvisorCard from '../components/AdvisorCard';
import AdvisorEntryBanner from '../components/advisor/AdvisorEntryBanner';
import HabitaScoreBadge from '../components/HabitaScoreBadge';
import HabitaScoreBreakdown from '../components/HabitaScoreBreakdown';
import DeepReasonsCard from '../components/DeepReasonsCard';

// Public location label — privacy-first: never expose the exact street address.
const publicLocation = (p) => {
  if (!p) return '';
  return p.neighborhood ? `${p.neighborhood}, ${p.city}` : (p.city || '');
};

// Build wa.me deep link: digits-only phone (default Colombia 57), preloaded text.
// Works for wa.me on mobile (opens app) and desktop (WhatsApp Web).
function buildWhatsAppLink(phone, property) {
  if (!phone) return null;
  let digits = String(phone).replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 10 && digits.startsWith('3')) digits = '57' + digits;
  const title = property?.title || 'la propiedad';
  const url = (typeof window !== 'undefined') ? window.location.href : '';
  const msg = `Hola, vi tu publicación en habita.co y estoy interesado en "${title}". ¿Podrías brindarme más información?${url ? `\n\n${url}` : ''}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
}

// Inline WhatsApp logo (no extra icon dep)
const WhatsAppIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
    <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.04 1.397-1.04 2.36 0 .945.387 1.804.978 2.55 1.247 1.578 2.81 2.95 4.652 3.756.7.301 1.516.673 2.288.673 1.05 0 2.135-.516 2.564-1.49.13-.302.215-.626.215-.958 0-.616-.45-.97-.96-1.13-.318-.103-1.13-.485-1.44-.485zm-3.04 9.295a11.93 11.93 0 0 1-6.18-1.7l-.43-.258-4.51 1.193 1.196-4.388-.276-.43a11.93 11.93 0 0 1-1.83-6.348c0-6.604 5.387-11.992 11.99-11.992 3.207 0 6.21 1.246 8.47 3.51a11.94 11.94 0 0 1 3.515 8.485c-.002 6.604-5.39 11.928-11.945 11.928zm0-25.5C8.05 1 1.5 7.55 1.5 15.57c0 2.555.668 5.05 1.94 7.255L1.5 30.5l7.84-2.06A14.34 14.34 0 0 0 16.07 30.5h.006c8.02 0 14.57-6.55 14.57-14.57.002-3.89-1.512-7.55-4.26-10.302C23.642 2.86 19.98 1 16.073 1z"/>
  </svg>
);

// Lazy-load Leaflet bundle (~150KB) so it doesn't impact LCP for users who never scroll down.
const PropertyMap = lazy(() => import('../components/PropertyMap'));
// Lazy-load similar properties to keep PropertyDetail's initial JS budget tight.
const SimilarProperties = lazy(() => import('../components/SimilarProperties'));

export default function PropertyDetail() {
  const { id } = useParams();

  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [habitaScore, setHabitaScore] = useState(null);
  const [deepReasons, setDeepReasons] = useState(null);

  const [active, setActive] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [fav, setFav] = useState(false);
  const [showInspect, setShowInspect] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.getProperty(id)
      .then(p => {
        setProperty(p);
        if (p?.id) {
          track(EventTypes.PROPERTY_VIEWED, { property_id: p.id, agent_id: p.owner_id });
          // Advisor: registrar la vista + características de la propiedad
          // como evidencia del interés del usuario.
          advisor.getOrStartContext({
            entryPoint: 'property_detail',
            intent: `interesado en ${p.type || 'propiedad'} en ${p.city || ''}`.trim(),
            payload: { first_property_id: p.id },
          }).then(async () => {
            advisor.updateContext('property_viewed', {
              property_id: p.id,
              type: p.type,
              operation: p.operation,
              price: p.price,
              city: p.city,
              neighborhood: p.neighborhood,
              beds: p.beds,
              baths: p.baths,
              area: p.area,
            });
            // Habita Score puntual para esta propiedad (audit P2)
            try {
              const ctxId = advisor.getActiveContextId();
              if (ctxId) {
                const res = await api.scoreForProperty({ context_id: ctxId, property_id: p.id });
                setHabitaScore(res?.habita_score || null);
                setDeepReasons(res?.deep_reasons || null);
              }
            } catch { /* silencioso */ }
          });
        }
      })
      .catch(() => setProperty(null))
      .finally(() => setLoading(false));
  }, [id]);

  // Dwell time: al desmontar, reportamos cuánto tiempo estuvo el usuario
  // viendo esta propiedad. Umbral mínimo 3s para descartar rebotes.
  useEffect(() => {
    if (!property?.id) return undefined;
    const startedAt = Date.now();
    return () => {
      const seconds = Math.round((Date.now() - startedAt) / 1000);
      if (seconds >= 3) {
        advisor.updateContext('property_dwell', { property_id: property.id, seconds });
      }
    };
  }, [property?.id]);

  // Cada vez que el usuario cambia la imagen activa, actualizamos el Context
  // con la imagen visualizada (debounced en cliente por el navegador nativo).
  useEffect(() => {
    if (!property?.id) return;
    advisor.updateContext('property_image_viewed', {
      property_id: property.id, image_index: active,
    });
  }, [active, property?.id]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-habita border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-gray-600">Propiedad no encontrada</p>
            <Button onClick={() => navigate('/')} className="btn-habita mt-4 rounded-full">Volver al inicio</Button>
          </div>
        </div>
      </div>
    );
  }

  const images = (property.images || []).map(u => imgUrl(u));
  const galleryThumbs = (property.images || []).map(u => thumbUrl(u, { w: 1200, h: 800 }));
  const agent = property.agent;

  const next = () => setActive((active + 1) % images.length);
  const prev = () => setActive((active - 1 + images.length) % images.length);

  const toggleFav = async () => {
    if (!user) { navigate('/login', { state: { from: `/propiedad/${id}` } }); return; }
    try {
      if (fav) {
        await api.removeFavorite(id);
        track(EventTypes.PROPERTY_UNFAVORITED, { property_id: id, source: 'detail' });
        advisor.updateContext('property_unfavorited', { property_id: id });
      } else {
        await api.addFavorite(id);
        track(EventTypes.PROPERTY_FAVORITED, { property_id: id, source: 'detail' });
        advisor.updateContext('property_favorited', { property_id: id });
      }
      setFav(!fav);
    } catch (err) {
      console.warn('toggleFav failed:', err?.message || err);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${property.title} — ${fmt(property.price)}`}
        description={`${property.beds} hab · ${property.baths} baños · ${property.area}m² · ${publicLocation(property)}. ${property.description.slice(0, 140)}...`}
        image={images[0]}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'RealEstateListing',
          name: property.title,
          description: property.description,
          url: typeof window !== 'undefined' ? window.location.href : '',
          image: images,
          offers: { '@type': 'Offer', price: property.price, priceCurrency: 'COP' },
          // Privacy: expose only locality (neighborhood + city), never the street.
          address: {
            '@type': 'PostalAddress',
            addressLocality: property.neighborhood || property.city,
            addressRegion: property.city,
            addressCountry: 'CO',
          },
          numberOfRooms: property.beds,
          floorSize: { '@type': 'QuantitativeValue', value: property.area, unitCode: 'MTK' },
        }}
      />
      <Header />

      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-6">
        <div className="grid grid-cols-4 gap-2 h-[280px] md:h-[480px] rounded-2xl overflow-hidden">
          <div className="col-span-4 md:col-span-2 row-span-2 relative cursor-pointer group" onClick={() => setShowGallery(true)}>
            <img src={galleryThumbs[0]} alt="" className="w-full h-full object-cover gallery-img" loading="eager" />
          </div>
          {galleryThumbs.slice(1, 5).map((img, i) => (
            <div key={i} className="hidden md:block col-span-1 relative cursor-pointer group overflow-hidden" onClick={() => { setActive(i + 1); setShowGallery(true); }}>
              <img src={img} alt="" className="w-full h-full object-cover gallery-img" loading="lazy" />
              {i === 3 && images.length > 5 && (
                <div className="absolute inset-0 bg-black/55 flex items-center justify-center text-white font-semibold">+{images.length - 5} fotos</div>
              )}
            </div>
          ))}
        </div>
        <button onClick={() => setShowGallery(true)} className="md:hidden mt-2 w-full bg-white border border-gray-200 py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-2">
          <Camera className="w-4 h-4" /> Ver todas las {images.length} fotos
        </button>
      </div>

      {property.videos && property.videos.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 lg:px-8 mt-6" data-testid="property-videos-section">
          <h2 className="font-serif text-xl font-bold text-habita-dark mb-3 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-habita text-white text-[10px] font-bold">▶</span>
            Recorrido en video
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {property.videos.map((v, i) => (
              <div
                key={v.public_id || v.url || i}
                className="rounded-2xl overflow-hidden bg-black aspect-video"
                data-testid={`property-video-${i}`}
              >
                <video
                  src={v.url}
                  poster={v.thumbnail || undefined}
                  controls
                  playsInline
                  preload="none"
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                {property.verified && (
                  <span data-testid="detail-verified-badge" className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                    <BadgeCheck className="w-3.5 h-3.5" /> Propiedad verificada
                  </span>
                )}
                {property.featured && (
                  <span className="inline-flex items-center bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                    Destacada
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-3" data-testid="property-public-location"><MapPin className="w-3.5 h-3.5" /> {publicLocation(property)}</p>
              <h1 className="font-serif text-4xl lg:text-5xl font-bold text-habita-dark mt-2 leading-tight">{property.title}</h1>
              <div className="mt-3 flex items-baseline gap-3 flex-wrap" data-testid="detail-price-block">
                {isDual(property) ? (
                  <>
                    <div className="flex flex-col">
                      <span className="font-serif text-4xl font-bold text-habita leading-tight">{formatCOP(getPrices(property).venta)}<span className="text-sm font-normal text-gray-500 ml-1">venta</span></span>
                      <span className="font-serif text-2xl font-bold text-habita-dark mt-1">{formatCOP(getPrices(property).arriendo)}<span className="text-sm font-normal text-gray-500 ml-1">/mes arriendo</span></span>
                    </div>
                    <span className="text-xs uppercase tracking-widest text-gray-500 ml-2" data-testid="detail-operation-label">Venta y Arriendo</span>
                  </>
                ) : (
                  <>
                    <p className="font-serif text-4xl font-bold text-habita">{formatCOP(property.price)}</p>
                    {property.operation === 'arriendo' && <span className="text-base text-gray-500">/mes</span>}
                    <span className="text-xs uppercase tracking-widest text-gray-500 ml-2" data-testid="detail-operation-label">{operationLabel(property)}</span>
                  </>
                )}
              </div>
              {isDual(property) && (
                <p className="text-xs text-gray-500 mt-1.5" data-testid="detail-dual-note">Un mismo inmueble disponible para Venta y Arriendo.</p>
              )}
              <div className="mt-5">
                <AdvisorCard
                  propertyId={property.id}
                  onContactClick={() => setShowContact(true)}
                />
              </div>
              <div className="mt-3">
                <AdvisorEntryBanner variant="detail" />
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button data-testid="detail-fav-btn" onClick={toggleFav} className="w-11 h-11 rounded-full border border-gray-200 hover:border-habita flex items-center justify-center transition-colors">
                <Heart className={`w-5 h-5 ${fav ? 'fill-rose-500 text-rose-500' : 'text-gray-700'}`} />
              </button>
              <ShareButton
                property={property}
                variant="icon"
                className="!w-11 !h-11 border border-gray-200 hover:border-habita !bg-white !shadow-none"
                testId="detail-share-btn"
              />
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: Bed, label: 'Habitaciones', value: property.beds },
              { icon: Bath, label: 'Baños', value: property.baths },
              { icon: Car, label: 'Parqueaderos', value: property.parking },
              { icon: Maximize, label: 'Área', value: `${property.area} m²` }
            ].map((s, i) => (
              <div key={i} className="bg-habita-mint rounded-xl p-4">
                <s.icon className="w-5 h-5 text-habita" />
                <p className="text-2xl font-bold mt-2 text-habita-dark">{s.value}</p>
                <p className="text-xs text-gray-600 uppercase tracking-widest mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          <section className="mt-10">
            <h2 className="font-serif text-2xl font-bold text-habita-dark">Sobre la propiedad</h2>
            <p className="text-gray-700 leading-relaxed mt-4 whitespace-pre-line">{property.description}</p>
          </section>

          {/* Habita Score breakdown para esta propiedad (audit P2) */}
          {habitaScore && (
            <section className="mt-10" data-testid="detail-habita-score-section">
              <div className="flex items-center gap-4 mb-4">
                <HabitaScoreBadge score={habitaScore} size="md" showLabel={false} />
                <div>
                  <h2 className="font-serif text-2xl font-bold text-habita-dark">Tu Habita Score aquí</h2>
                  <p className="text-sm text-gray-600 leading-snug">{habitaScore.headline}</p>
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-5">
                <HabitaScoreBreakdown score={habitaScore} />
              </div>
              {deepReasons && (
                <div className="mt-4">
                  <DeepReasonsCard reasons={deepReasons} />
                </div>
              )}
            </section>
          )}

          {property.features?.length > 0 && (
            <section className="mt-10">
              <h2 className="font-serif text-2xl font-bold text-habita-dark">Características</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
                {property.features.map(f => (
                  <div key={f} className="flex items-center gap-2 text-sm text-gray-700">
                    <div className="w-6 h-6 rounded-full bg-habita-mint flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-habita" /></div>
                    {f}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="mt-10">
            <h2 className="font-serif text-2xl font-bold text-habita-dark">Detalles</h2>
            <dl className="mt-5 grid grid-cols-2 gap-y-3 gap-x-8 text-sm">
              {[
                ['Tipo', property.type?.charAt(0).toUpperCase() + property.type?.slice(1)],
                ['Estrato', property.stratum || '—'],
                ['Año construcción', property.year || '—'],
                ['Área construida', `${property.area} m²`],
                ['Ciudad', property.city],
                ['Barrio', property.neighborhood || '—'],
                ...(evChargerLabel(property) ? [['⚡ Cargador vehículo eléctrico', evChargerLabel(property)]] : []),
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-gray-100 pb-2">
                  <dt className="text-gray-500">{k}</dt>
                  <dd className="font-semibold text-gray-900">{v}</dd>
                </div>
              ))}
            </dl>
          </section>

          {property.inspections?.length > 0 && (
            <section className="mt-10">
              <h2 className="font-serif text-2xl font-bold text-habita-dark">Inspecciones programadas</h2>
              <p className="text-sm text-gray-600 mt-2">Visita la propiedad en cualquiera de estas fechas o agenda una privada.</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {property.inspections.map((ins, i) => (
                  <button
                    key={i}
                    onClick={() => setShowInspect(true)}
                    className="text-left p-4 bg-white border border-gray-200 hover:border-habita rounded-xl transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-habita-mint flex flex-col items-center justify-center shrink-0">
                        <span className="text-[10px] text-habita uppercase font-semibold">{new Date(ins.date).toLocaleDateString('es-CO', { month: 'short' })}</span>
                        <span className="text-lg font-bold text-habita-dark leading-none">{new Date(ins.date).getDate()}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-habita-dark">{new Date(ins.date).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                        <p className="text-sm text-gray-600 mt-0.5">{ins.time}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
          <Button onClick={() => setShowInspect(true)} className="btn-habita mt-5 rounded-full px-7 h-12">
            <Calendar className="w-4 h-4 mr-2" /> Agendar inspección
          </Button>

          {property.lat && property.lng ? (
            <section className="mt-10" data-testid="property-location-section">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-serif text-2xl font-bold text-habita-dark">Ubicación</h2>
                {property.coords_source && property.coords_source !== 'precise' && (
                  <span
                    data-testid="coords-approx-badge"
                    className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1"
                    title={property.coords_source === 'centroid'
                      ? 'Ubicación al centro del municipio'
                      : 'Ubicación aproximada al barrio'}
                  >
                    <MapPin className="w-3 h-3" /> Ubicación aproximada
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-2 flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-habita" />
                {property.neighborhood ? `${property.neighborhood}, ` : ''}{property.city}
              </p>
              <div className="mt-5">
                <Suspense fallback={
                  <div className="aspect-[16/9] rounded-2xl bg-gray-100 animate-pulse flex items-center justify-center">
                    <MapPin className="w-8 h-8 text-gray-300" />
                  </div>
                }>
                  <PropertyMap
                    lat={property.lat}
                    lng={property.lng}
                    label={`${property.neighborhood || ''}${property.neighborhood ? ', ' : ''}${property.city}`}
                  />
                </Suspense>
              </div>
              {property.coords_source === 'centroid' && (
                <p className="text-xs text-gray-500 mt-2" data-testid="coords-centroid-disclaimer">
                  Mapa al centro del municipio — el inmueble está en esta zona pero la ubicación exacta se confirma con el agente.
                </p>
              )}
            </section>
          ) : property.address && property.city ? (
            <section className="mt-10" data-testid="property-location-section-fallback">
              <h2 className="font-serif text-2xl font-bold text-habita-dark">Ubicación</h2>
              <div className="mt-3 p-5 rounded-2xl bg-habita-cream flex items-start gap-3">
                <MapPin className="w-5 h-5 text-habita shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-habita-dark">{property.neighborhood || property.city}</p>
                  <p className="text-sm text-gray-600 mt-0.5">{property.city}</p>
                  <p className="text-xs text-gray-500 mt-2">Mapa interactivo no disponible para esta propiedad.</p>
                </div>
              </div>
            </section>
          ) : null}

          <section className="mt-10 bg-habita-cream rounded-2xl p-6 lg:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-serif text-2xl font-bold text-habita-dark flex items-center gap-2"><Calculator className="w-6 h-6" /> Calculadora de cuota</h2>
                <p className="text-sm text-gray-600 mt-2">Estima tu cuota mensual con un crédito hipotecario.</p>
              </div>
              <Link to="/calculadora" className="text-sm font-semibold text-habita underline">Abrir calculadora completa</Link>
            </div>
            <MortgageMini price={property.price} />
          </section>

          {/* Report property — discrete footer action */}
          <div className="mt-10 pt-6 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-xs text-gray-500">
              <p>Código de referencia: <span className="font-mono text-gray-700">{property.id.slice(0, 8).toUpperCase()}</span></p>
              <p className="mt-1">¿Encontraste algo sospechoso o incorrecto?</p>
            </div>
            <button
              data-testid="report-btn"
              onClick={() => setShowReport(true)}
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-rose-600 font-medium px-4 h-10 rounded-full border border-gray-200 hover:border-rose-200 transition-colors w-fit"
            >
              <Flag className="w-4 h-4" /> Reportar publicación
            </button>
          </div>

          {/* Similar / nearby properties — lazy-loaded via IntersectionObserver */}
          <Suspense fallback={null}>
            <SimilarProperties propertyId={property.id} />
          </Suspense>
        </div>

        <aside className="lg:sticky lg:top-24 self-start">
          {agent && (
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="property-agent-card">
              {agent.slug ? (
                <Link
                  to={`/agente/${agent.slug}`}
                  onClick={() => track(EventTypes.PROPERTY_AGENT_CLICKED, { property_id: property.id, agent_id: agent.id, source: 'detail_sidebar_header' })}
                  className="flex items-center gap-4 group focus:outline-none rounded-xl -m-1 p-1 hover:bg-habita-mint/30 transition-colors"
                  data-testid="property-agent-link"
                >
                  <div className="relative">
                    <img src={agent.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name)}&background=00824D&color=fff`} alt={agent.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-habita-mint" />
                    {agent.verified && (
                      <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-habita text-white flex items-center justify-center ring-2 ring-white" title="Agente verificado">
                        <BadgeCheck className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-habita-dark truncate group-hover:text-habita transition-colors" data-testid="property-agent-name">{agent.name}</p>
                    <p className="text-xs text-gray-500 truncate">{agent.title || (agent.role === 'agent' ? 'Asesor' : 'Propietario')}</p>
                    {agent.verified && (
                      <p className="text-xs text-habita font-semibold flex items-center gap-1 mt-0.5">
                        <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> {agent.rating || '5.0'} · Agente verificado
                      </p>
                    )}
                  </div>
                </Link>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <img src={agent.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name)}&background=00824D&color=fff`} alt={agent.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-habita-mint" />
                    {agent.verified && (
                      <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-habita text-white flex items-center justify-center ring-2 ring-white" title="Agente verificado">
                        <BadgeCheck className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-habita-dark truncate" data-testid="property-agent-name">{agent.name}</p>
                    <p className="text-xs text-gray-500 truncate">{agent.title || (agent.role === 'agent' ? 'Asesor' : 'Propietario')}</p>
                    {agent.verified && (
                      <p className="text-xs text-habita font-semibold flex items-center gap-1 mt-0.5">
                        <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> {agent.rating || '5.0'} · Agente verificado
                      </p>
                    )}
                  </div>
                </div>
              )}

              {agent.agency_ref && (
                <Link
                  to={`/inmobiliaria/${agent.agency_ref.slug}`}
                  onClick={() => track(EventTypes.PROPERTY_AGENCY_CLICKED, { property_id: property.id, agency_id: agent.agency_ref.id })}
                  className="mt-3 flex items-center gap-2 text-xs text-gray-600 hover:text-habita transition-colors group"
                  data-testid="property-agent-agency-link"
                >
                  {agent.agency_ref.logo_url
                    ? <img src={agent.agency_ref.logo_url} alt="" className="w-5 h-5 rounded object-cover" />
                    : <Building2 className="w-3.5 h-3.5" />}
                  <span>Miembro de <span className="font-semibold underline-offset-2 group-hover:underline">{agent.agency_ref.name}</span></span>
                  {agent.agency_ref.verified && <BadgeCheck className="w-3 h-3 text-emerald-600 shrink-0" />}
                </Link>
              )}

              {agent.slug && (
                <Link
                  to={`/agente/${agent.slug}`}
                  onClick={() => track(EventTypes.PROPERTY_AGENT_CLICKED, { property_id: property.id, agent_id: agent.id, source: 'detail_sidebar_cta' })}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-habita hover:underline"
                  data-testid="property-agent-cta"
                >
                  <PlayCircle className="w-3.5 h-3.5" /> Ver perfil completo →
                </Link>
              )}

              <div className="mt-5 space-y-2">
                {(() => {
                  const wa = buildWhatsAppLink(agent.phone, property);
                  return wa ? (
                    <a
                      href={wa}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="whatsapp-btn"
                      onClick={() => { track(EventTypes.CONTACT_WHATSAPP, { property_id: property.id, agent_id: property.owner_id, source: 'detail_sidebar' }); advisor.updateContext('contact_intent', { property_id: property.id, channel: 'whatsapp', source: 'detail_sidebar' }); }}
                      className="flex items-center justify-center gap-2 w-full h-12 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold shadow-sm transition-colors"
                    >
                      <WhatsAppIcon className="w-5 h-5" /> Contactar por WhatsApp
                    </a>
                  ) : null;
                })()}
                <Button data-testid="contact-btn" onClick={() => { setShowContact(true); advisor.updateContext('contact_intent', { property_id: property.id, channel: 'message', source: 'detail_sidebar' }); }} variant="outline" className="w-full h-12 rounded-full border-gray-300 text-gray-700 hover:bg-gray-50">
                  <Mail className="w-4 h-4 mr-2" /> Mensaje en habita
                </Button>
                <Button onClick={() => { setShowInspect(true); advisor.updateContext('visit_intent', { property_id: property.id, source: 'detail_sidebar' }); }} variant="outline" className="w-full h-12 rounded-full border-habita text-habita hover:bg-habita-mint">
                  <Calendar className="w-4 h-4 mr-2" /> Agendar visita
                </Button>
              </div>
              <p className="text-[11px] text-gray-400 mt-4 text-center">Código: {property.id.slice(0, 8).toUpperCase()}</p>
            </div>
          )}
        </aside>
      </div>

      {/* MOBILE STICKY CTA */}
      {agent && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 px-4 py-3 flex items-center gap-2 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]" data-testid="mobile-sticky-cta">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-widest text-gray-500">{isDual(property) ? 'Venta / Arriendo' : 'Precio'}</p>
            <p className="font-serif text-lg font-bold text-habita leading-tight truncate">
              {isDual(property) ? (
                <>{formatCOP(getPrices(property).venta)} <span className="text-xs font-normal text-gray-500">· {formatCOP(getPrices(property).arriendo)}/mes</span></>
              ) : (
                <>{formatCOP(property.price)}{property.operation === 'arriendo' && <span className="text-xs font-normal text-gray-500"> /mes</span>}</>
              )}
            </p>
          </div>
          <Button onClick={() => { setShowInspect(true); advisor.updateContext('visit_intent', { property_id: property.id, source: 'mobile_sticky' }); }} variant="outline" size="sm" className="rounded-full border-habita text-habita hover:bg-habita-mint h-10 px-3 shrink-0" aria-label="Agendar visita">
            <Calendar className="w-4 h-4" />
          </Button>
          {(() => {
            const wa = buildWhatsAppLink(agent.phone, property);
            return wa ? (
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp-btn"
                onClick={() => { track(EventTypes.CONTACT_WHATSAPP, { property_id: property.id, agent_id: property.owner_id, source: 'mobile_sticky' }); advisor.updateContext('contact_intent', { property_id: property.id, channel: 'whatsapp', source: 'mobile_sticky' }); }}
                className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold text-sm shadow-sm shrink-0"
              >
                <WhatsAppIcon className="w-4 h-4" /> WhatsApp
              </a>
            ) : (
              <Button onClick={() => { setShowContact(true); advisor.updateContext('contact_intent', { property_id: property.id, channel: 'message', source: 'mobile_sticky' }); }} size="sm" className="btn-habita rounded-full h-10 px-4 shrink-0" data-testid="mobile-contact-btn">
                <Mail className="w-4 h-4 mr-1.5" /> Contactar
              </Button>
            );
          })()}
        </div>
      )}

      {showGallery && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          <div className="flex items-center justify-between p-5 text-white">
            <span className="text-sm">{active + 1} / {images.length}</span>
            <button onClick={() => setShowGallery(false)} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 flex items-center justify-center relative px-4">
            <button onClick={prev} className="absolute left-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"><ChevronLeft className="w-6 h-6" /></button>
            <img src={images[active]} alt="" className="max-h-[78vh] max-w-full object-contain" />
            <button onClick={next} className="absolute right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"><ChevronRight className="w-6 h-6" /></button>
          </div>
          <div className="p-4 flex gap-2 overflow-x-auto no-scrollbar">
            {images.map((img, i) => (
              <button key={i} onClick={() => setActive(i)} className={`shrink-0 w-20 h-14 rounded-lg overflow-hidden ${i === active ? 'ring-2 ring-amber-400' : 'opacity-60'}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {showInspect && <InspectionModal property={property} onClose={() => setShowInspect(false)} />}
      {showContact && agent && <ContactAgentModal agent={agent} property={property} onClose={() => setShowContact(false)} />}
      {showReport && <ReportPropertyModal propertyId={property.id} onClose={() => setShowReport(false)} />}

      {/* Bottom padding on mobile so sticky CTA doesn't cover footer */}
      <div className="lg:hidden h-20" />

      <Footer />
    </div>
  );
}

function MortgageMini({ price }) {
  const [years, setYears] = useState(15);
  const [rate, setRate] = useState(11.5);
  const [down, setDown] = useState(30);
  const principal = price * (1 - down / 100);
  const r = rate / 100 / 12;
  const n = years * 12;
  const monthly = principal > 0 && r > 0 ? (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : 0;

  return (
    <div className="mt-5 grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Cuota inicial: {down}%</label>
          <input type="range" min="10" max="70" value={down} onChange={(e) => setDown(+e.target.value)} className="w-full accent-[#00824D] mt-2" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Plazo: {years} años</label>
          <input type="range" min="5" max="30" value={years} onChange={(e) => setYears(+e.target.value)} className="w-full accent-[#00824D] mt-2" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Tasa: {rate}% EA</label>
          <input type="range" min="7" max="20" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)} className="w-full accent-[#00824D] mt-2" />
        </div>
      </div>
      <div className="bg-white rounded-xl p-5 border border-habita-mint">
        <p className="text-xs uppercase tracking-widest text-gray-500">Cuota mensual estimada</p>
        <p className="font-serif text-4xl font-bold text-habita mt-2">{formatCOP(Math.round(monthly))}</p>
        <div className="mt-4 text-sm space-y-1.5 text-gray-700">
          <div className="flex justify-between"><span>Cuota inicial</span><span className="font-medium">{formatCOP(price * down / 100)}</span></div>
          <div className="flex justify-between"><span>Monto a financiar</span><span className="font-medium">{formatCOP(principal)}</span></div>
          <div className="flex justify-between"><span>Total a pagar</span><span className="font-medium">{formatCOP(Math.round(monthly * n))}</span></div>
        </div>
      </div>
    </div>
  );
}
